// src/pages/BlogPage.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from "../api/axiosInstance";
import {useNavigate} from "react-router-dom";

const API_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://localhost:8080'
    : 'https://api.sonabelanska.sk';

const PageContainer = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%);
    padding: 2rem 0;
    padding-top: 150px;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const PageHeader = styled.div`
    text-align: center;
    margin-bottom: 3rem;
`;

const PageTitle = styled(motion.h1)`
    font-size: 3rem;
    font-weight: 800;
    color: #000000;
    line-height: 1.1;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }

    @media (max-width: 480px) {
        font-size: 2rem;
    }
`;

const PageSubtitle = styled(motion.p)`
    font-size: 1.2rem;
    color: #4a5568;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: 768px) {
        font-size: 1.1rem;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4rem;
    color: #64748b;
    font-size: 1.1rem;
`;

const ErrorContainer = styled.div`
    text-align: center;
    padding: 3rem;
    color: #dc2626;
    background: #fef2f2;
    border-radius: 12px;
    margin: 2rem 0;
`;

const BlogGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
    }

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 1.5rem;
        max-width: 400px;
        margin: 0 auto;
    }
`;

const BlogCard = styled(motion.article)`
    background: #ffffff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    height: 100%;
    cursor: pointer;

    &:hover {
        box-shadow: 0 8px 30px rgba(153, 1, 71, 0.15);
        transform: translateY(-5px);
    }
`;

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 220px;
    overflow: hidden;
    background: #f1f5f9;
`;

const BlogImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;

    ${BlogCard}:hover & {
        transform: scale(1.05);
    }
`;

const PlaceholderImage = styled.div`
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    font-size: 0.9rem;
`;

const CategoryTag = styled.span`
    position: absolute;
    top: 1rem;
    left: 1rem;
    padding: 0.4rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #ffffff;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const BlogContent = styled.div`
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

const BlogTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 700;
    color: #000000;
    margin-bottom: 0.75rem;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const BlogExcerpt = styled.p`
    font-size: 0.95rem;
    color: #4a5568;
    line-height: 1.5;
    margin-bottom: 1.25rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex-grow: 1;
`;

const BlogMeta = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const MetaItem = styled.span`
    background: #f8f9fa;
    padding: 0.4rem 0.8rem;
    border-radius: 12px;
    font-size: 0.8rem;
    color: #4a5568;
    font-weight: 500;
`;

const ReadMoreButton = styled(motion.button)` // Changed from <a> to <button>
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 1.5rem;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: #ffffff;
    border-radius: 10px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    border: none;
    cursor: pointer;
    width: 100%;

    &:hover {
        box-shadow: 0 4px 15px rgba(153, 1, 71, 0.3);
        transform: translateY(-2px);
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 4rem 2rem;
    color: #64748b;
    
    h3 {
        font-size: 1.5rem;
        margin-bottom: 1rem;
        color: #475569;
    }
`;

// Types for the API response
interface Category {
    id: number;
    name: string;
}

interface BlogPost {
    id: number;
    title: string;
    excerpt: string;
    featuredImage: string;
    category: Category;
    readTime: string;
    createdAt: string;
    updatedAt: string;
}
const BlogPage: React.FC = () => {
    const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate(); // Add this hook

    // Format date to Slovak format
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        };
        return date.toLocaleDateString('sk-SK', options);
    };

    // Get relative time for recent posts
    const getRelativeTime = (dateString: string): string => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

        if (diffInHours < 24) {
            return 'Dnes';
        } else if (diffInHours < 48) {
            return 'Včera';
        } else if (diffInHours < 168) { // 7 days
            return `Pred ${Math.floor(diffInHours / 24)} dňami`;
        } else {
            return formatDate(dateString);
        }
    };

    // Fetch blog posts from backend
    const fetchBlogPosts = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get(`${API_BASE_URL}/api/posts`);
            setBlogPosts(response.data);
        } catch (err: any) {
            console.error('Error fetching blog posts:', err);
            setError('Nepodarilo sa načítať články. Skúste to prosím neskôr.');
        } finally {
            setLoading(false);
        }
    };

    // Handle read more click
    const handleReadMore = (postId: number) => {
        navigate(`/blog/${postId}`);
    };

    useEffect(() => {
        fetchBlogPosts();
    }, []);

    if (loading) {
        return (
            <PageContainer>
                <Container>
                    <PageHeader>
                        <PageTitle
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            Blog
                        </PageTitle>
                        <PageSubtitle
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                        >
                            Načítavame najnovšie články...
                        </PageSubtitle>
                    </PageHeader>
                    <LoadingContainer>
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            style={{
                                width: '40px',
                                height: '40px',
                                border: '3px solid #e2e8f0',
                                borderTop: '3px solid #990147',
                                borderRadius: '50%',
                                marginRight: '1rem'
                            }}
                        />
                        Načítavanie článkov...
                    </LoadingContainer>
                </Container>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer>
                <Container>
                    <PageHeader>
                        <PageTitle
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            Blog
                        </PageTitle>
                    </PageHeader>
                    <ErrorContainer>
                        <h3>Chyba pri načítavaní</h3>
                        <p>{error}</p>
                        <motion.button
                            onClick={fetchBlogPosts}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            style={{
                                marginTop: '1rem',
                                padding: '0.75rem 1.5rem',
                                background: '#990147',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer'
                            }}
                        >
                            Skúsiť znova
                        </motion.button>
                    </ErrorContainer>
                </Container>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Container>
                <PageHeader>
                    <PageTitle
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        Blog
                    </PageTitle>
                    <PageSubtitle
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        Objavte naše články o duševnom zdraví, výžive, spánku a životnom štýle.
                        Každý článok je navrhnutý tak, aby vám poskytol užitočné rady a inšpiráciu pre lepší život.
                    </PageSubtitle>
                </PageHeader>

                {blogPosts.length === 0 ? (
                    <EmptyState>
                        <h3>Žiadne články</h3>
                        <p>Zatiaľ neboli pridané žiadne články. Skúste to prosím neskôr.</p>
                    </EmptyState>
                ) : (
                    <BlogGrid>
                        {blogPosts.map((post, index) => (
                            <BlogCard
                                key={post.id}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                whileHover={{ y: -5 }}
                            >
                                <ImageWrapper>
                                    {post.featuredImage ? (
                                        <BlogImage
                                            src={post.featuredImage}
                                            alt={post.title}
                                            loading="lazy"
                                            onError={(e) => {
                                                // Fallback if image fails to load
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                                target.nextSibling && ((target.nextSibling as HTMLElement).style.display = 'flex');
                                            }}
                                        />
                                    ) : null}
                                    <PlaceholderImage style={{ display: post.featuredImage ? 'none' : 'flex' }}>
                                        📝 Blog
                                    </PlaceholderImage>
                                    <CategoryTag>{post.category.name}</CategoryTag>
                                </ImageWrapper>
                                <BlogContent>
                                    <BlogTitle>{post.title}</BlogTitle>
                                    <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                                    <BlogMeta>
                                        <MetaItem>{getRelativeTime(post.createdAt)}</MetaItem>
                                        <MetaItem>{post.readTime || '5 min'}</MetaItem>
                                    </BlogMeta>
                                    <ReadMoreButton
                                        onClick={() => handleReadMore(post.id)} // Changed to onClick
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        Čítať Viac
                                    </ReadMoreButton>
                                </BlogContent>
                            </BlogCard>
                        ))}
                    </BlogGrid>
                )}
            </Container>
        </PageContainer>
    );
};

export default BlogPage;